/**
 * The whole field, priced, shortest first.
 *
 * The board used to be a list of markets in the order they were posted, which
 * is the order the bookie happened to type them in and tells a reader nothing.
 * What everybody actually wants is the same thing every sportsbook shows: the
 * roster, favorite at the top, longest price at the bottom, so the shape of
 * the field is legible at a glance.
 *
 * Sorted on implied probability rather than on the raw number, because American
 * odds do not sort: -150 is shorter than +120, and +120 is shorter than +2000,
 * so the sign has to be read before the magnitude means anything.
 *
 * Players with no line still appear. A missing price is information — it is the
 * bookie's to-do list, and on a board that hid them nobody would notice.
 */

import { useState } from 'react'
import { ODDS_2025 } from '../data/odds-2025'
import type { Tournament } from '../engine/types'
import { formatAmerican, impliedProbability } from '../engine/odds'
import type { Book, OddsMarket, OddsPoint } from '../store'

const OUTRIGHT = 'outright'

/**
 * The line's movement, drawn small.
 *
 * A sparkline rather than a chart: this sits inside a table row on a phone, and
 * the question it answers is "which way has he been going", not "what exactly
 * was he on Tuesday". The numbers either side give the precision.
 *
 * Plotted on implied probability, for the same reason the sort is: a line
 * crossing from +110 to -110 is a small move in money and a huge one in the raw
 * number, and a graph drawn on the raw number would show a cliff that is not
 * there.
 */
function PriceChart({ points }: { points: OddsPoint[] }) {
  const [picked, setPicked] = useState<number | null>(null)
  if (points.length < 2) return null

  const probs = points.map((p) => impliedProbability(p.american_odds))
  const times = points.map((p) => Date.parse(p.posted_at))
  const lo = Math.min(...probs)
  const hi = Math.max(...probs)
  const t0 = Math.min(...times)
  const ySpan = hi - lo || 1
  const tSpan = Math.max(...times) - t0 || 1

  /*
   * Percent coordinates with padding, so a dot on the first or last price is
   * never clipped by the frame. X is TIME, not the point's index: three moves
   * in one evening and one a day later should look like exactly that.
   */
  const xPct = (i: number) => 6 + ((times[i]! - t0) / tSpan) * 88
  const yPct = (i: number) => 14 + (1 - (probs[i]! - lo) / ySpan) * 72
  const d = points
    .map((_, i) => `${i === 0 ? 'M' : 'L'}${xPct(i).toFixed(1)},${yPct(i).toFixed(1)}`)
    .join(' ')

  const when = (iso: string): string => {
    const at = new Date(iso)
    return `${at.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} · ${at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
  }

  return (
    <div>
      {/*
       * The line and its dots share the signal color from the wrapper; the
       * dots are HTML buttons rather than SVG circles so they keep their
       * shape at any width, carry a real 44px hit area (negative-margin
       * style, like every dense tap in the app), and read to a screen
       * reader as the price they hold.
       */}
      <div className="relative h-24">
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full"
          aria-hidden="true"
        >
          <path
            d={d}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        {points.map((p, i) => (
          <button
            key={`${p.posted_at}-${i}`}
            type="button"
            aria-label={`${formatAmerican(p.american_odds)}, posted ${when(p.posted_at)}`}
            aria-pressed={picked === i}
            onClick={() => setPicked(picked === i ? null : i)}
            className="bc-tap absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
            style={{ left: `${xPct(i)}%`, top: `${yPct(i)}%` }}
          >
            <span
              className={`block w-2.5 h-2.5 rounded-full border-2 border-current ${
                picked === i ? 'bg-current' : 'bg-paper'
              }`}
            />
          </button>
        ))}
      </div>
      {picked != null ? (
        <p className="text-[12px] text-ink pt-1">
          <b className="bc-num">{formatAmerican(points[picked]!.american_odds)}</b>
          <span className="text-mute"> — posted {when(points[picked]!.posted_at)}</span>
        </p>
      ) : (
        <p className="text-[12px] text-dim pt-1">
          Tap a point for the price and when it was posted.
        </p>
      )}
    </div>
  )
}

export function FieldBoard({
  doc,
  book,
  isAdmin,
  onQuote,
  onArchive,
  children,
}: {
  doc: Tournament
  book: Book
  isAdmin: boolean
  /** Opens the bulk quote editor. */
  onQuote: () => void
  /** Take an orphaned line off the board. Refused if money is still on it. */
  onArchive?: (oddsId: string) => void
  /** The bet controls for the open row, rendered by the parent. */
  children?: (market: OddsMarket) => React.ReactNode
}) {
  const [open, setOpen] = useState<string | null>(null)
  const [showOut, setShowOut] = useState(false)

  // One open outright per player. A settled market is last year's news and
  // must not be mistaken for the current price.
  const lineFor = new Map<string, OddsMarket>()
  for (const o of book.odds) {
    if (o.market !== OUTRIGHT || !o.subject_id || o.status === 'settled') continue
    lineFor.set(o.subject_id, o)
  }

  /*
   * A man whose outright has been SETTLED is out of the tournament, not
   * waiting on a price.
   *
   * Both states arrive here as `market: undefined` — `lineFor` skips settled
   * markets — so an eliminated man rendered identically to one the bookie had
   * simply not got to yet: a name, a dash, and an invitation to quote him. On
   * Thursday morning that left nine dead men sitting under the four who can
   * still win it, each looking like an unfinished job.
   *
   * Settled-and-lost is knowledge, so it gets its own fold at the foot of the
   * table rather than a row in the middle of the live board.
   */
  const decided = new Set(
    book.odds
      .filter((o) => o.market === OUTRIGHT && o.subject_id && o.status === 'settled')
      .map((o) => o.subject_id as string),
  )

  const all = doc.players
    .filter((p) => p.active)
    .map((p) => ({ player: p, market: lineFor.get(p.id), out: decided.has(p.id) }))
    .sort((a, b) => {
      // Unpriced players sit at the bottom: they are a job, not a price.
      if (!a.market && !b.market) return a.player.displayName.localeCompare(b.player.displayName)
      if (!a.market) return 1
      if (!b.market) return -1
      return (
        impliedProbability(b.market.american_odds) - impliedProbability(a.market.american_odds)
      )
    })

  const rows = all.filter((r) => !r.out)
  const eliminated = all.filter((r) => r.out)

  const historyFor = (id: string): OddsPoint[] =>
    (book.history ?? []).filter((h) => h.odds_id === id)

  const priced = rows.filter((r) => r.market).length

  /*
   * Lines whose player is no longer in the field.
   *
   * Removing somebody from the roster does not touch his odds, so his market
   * stayed open and bettable while vanishing from this table — invisible to the
   * bookie, still counted in the exposure. A line with nobody to win it cannot
   * be settled and has to be dealt with, so it gets its own section rather than
   * being quietly dropped.
   */
  const active = new Set(doc.players.filter((p) => p.active).map((p) => p.id))
  const orphans = book.odds.filter(
    (o) =>
      o.market === OUTRIGHT &&
      o.subject_id != null &&
      o.status !== 'settled' &&
      !active.has(o.subject_id),
  )

  return (
    <>
      <div className="px-4 py-2.5 border-b border-rule flex items-baseline justify-between gap-3">
        <span className="bc-label">The field</span>
        <span className="text-[12px] text-mute">
          {rows.length === doc.players.filter((p) => p.active).length
            ? `${priced} of ${rows.length} priced`
            : `${rows.length} still alive`}
        </span>
      </div>

      {isAdmin && (
        <button
          type="button"
          onClick={onQuote}
          className="bc-tap w-full bg-ink text-paper bc-label py-3"
        >
          {priced === 0 ? 'Price the field' : 'Move the lines'}
        </button>
      )}

      <ul className="divide-y divide-rule border-b border-rule">
        {rows.map(({ player, market }) => {
          const isOpen = open === player.id
          const history = market ? historyFor(market.id) : []
          const first = history[0]?.american_odds ?? 0
          const last = history[history.length - 1]?.american_odds ?? 0
          const moved = history.length > 1 ? last - first : 0
          return (
            <li key={player.id}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : player.id)}
                className="w-full text-left px-4 py-3 grid grid-cols-[minmax(0,1fr)_auto_auto] gap-3 items-center bc-tap"
              >
                <span className="bc-name text-sm truncate">{player.displayName}</span>
                {/*
                  Direction, not magnitude. A shortening price is the story on a
                  board like this — somebody has been playing well, or somebody
                  has been betting him.
                */}
                <span className="text-[12px] text-mute w-14 text-right">
                  {history.length > 1
                    ? moved === 0
                      ? 'unmoved'
                      : impliedProbability(last) > impliedProbability(first)
                        ? 'shorter'
                        : 'drifting'
                    : ''}
                </span>
                <span className="bc-num text-base w-16 text-right">
                  {market ? formatAmerican(market.american_odds) : <span className="text-dim">—</span>}
                </span>
              </button>

              {isOpen && (
                <div className="px-4 pb-3">
                  {market ? (
                    <>
                      {history.length > 1 ? (
                        <div>
                          <div className="text-signal">
                            <PriceChart points={history} />
                          </div>
                          <div className="flex justify-between text-[11px] text-mute pt-0.5">
                            <span>opened {formatAmerican(first)}</span>
                            <span>
                              {history.length} price{history.length === 1 ? '' : 's'}
                            </span>
                            <span>now {formatAmerican(last)}</span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-[12px] text-mute">
                          Opened at {formatAmerican(market.american_odds)} and has not moved.
                        </p>
                      )}
                      {/* Last year's price is a data point, not a prophecy —
                          but it is the first thing the table argues about. */}
                      {ODDS_2025[player.id] != null && (
                        <p className="text-[12px] text-mute pt-1">
                          Went off at {formatAmerican(ODDS_2025[player.id]!)} last year.
                        </p>
                      )}
                      {children?.(market)}
                    </>
                  ) : (
                    <p className="text-[12px] text-mute leading-snug">
                      No price on {player.displayName} yet.
                      {isAdmin ? ' Use “Price the field” above.' : ' The bookie has not put one up.'}
                      {ODDS_2025[player.id] != null &&
                        ` Went off at ${formatAmerican(ODDS_2025[player.id]!)} last year.`}
                    </p>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {/*
        The men who are out, folded away.

        They are kept rather than hidden outright because a settled outright is
        the record of somebody's money — and the fold answers the question the
        board would otherwise raise, which is "where did everybody go".
      */}
      {eliminated.length > 0 && (
        <div className="border-b border-rule">
          <button
            type="button"
            onClick={() => setShowOut((v) => !v)}
            aria-expanded={showOut}
            className="bc-tap w-full px-4 py-2.5 flex items-center justify-between gap-2 text-[13px] text-mute"
          >
            <span>
              {eliminated.length} out of it &mdash;{showOut ? ' hide' : ' show'}
            </span>
            <span aria-hidden className="text-[11px]">
              {showOut ? '▴' : '▾'}
            </span>
          </button>
          {showOut && (
            <ul className="divide-y divide-rule border-t border-rule">
              {eliminated.map(({ player }) => (
                <li
                  key={player.id}
                  className="px-4 py-2.5 flex items-center justify-between gap-3"
                >
                  <span className="bc-name text-sm truncate text-mute">
                    {player.displayName}
                  </span>
                  <span className="bc-label text-[10px] text-mute shrink-0">Settled</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {orphans.length > 0 && (
        <>
          <div className="px-4 py-2.5 border-b border-rule bg-signal text-paper">
            <p className="bc-label">
              {orphans.length === 1 ? 'A line with no player' : `${orphans.length} lines with no player`}
            </p>
            <p className="text-[13px] leading-snug mt-1">
              {orphans.length === 1 ? 'This man is' : 'These men are'} no longer in the field, so
              {orphans.length === 1 ? ' his line' : ' their lines'} can never be settled — but
              {orphans.length === 1 ? ' it is' : ' they are'} still open and still counted in the
              exposure below.
            </p>
          </div>
          <ul className="divide-y divide-rule border-b border-rule">
            {orphans.map((o) => (
              <li
                key={o.id}
                className="px-4 py-3 grid grid-cols-[minmax(0,1fr)_auto_auto] gap-3 items-center"
              >
                <span className="bc-name text-sm truncate">
                  {o.subject_id}
                  <span className="text-mute"> · not in the field</span>
                </span>
                <span className="bc-num text-base tabular-nums">
                  {formatAmerican(o.american_odds)}
                </span>
                {onArchive && (
                  <button
                    type="button"
                    onClick={() => onArchive(o.id)}
                    className="bc-tap bc-label px-3 py-2 border-2 border-ink"
                  >
                    Archive
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}
